import {Box, Card, Group, Stack, Text, Title, Tooltip} from "@mantine/core";
import type {StudyStats} from "@/app/storage";
import {formatHeatmapDate, generateDateRange, getDayOfWeek} from "@/app/utils/dateUtils";

export function ActivityHeatmap(props: {studyStats: StudyStats | null}) {
  const {studyStats} = props;
  // Generate last 12 weeks of dates
  const dates = generateDateRange(12);

  // Get activity count for each date
  const getActivityCount = (date: Date): number => {
    if (!studyStats?.activityData) return 0;
    const dateStr = date.toISOString().split("T")[0];
    if (!dateStr) return 0;
    return studyStats.activityData.get(dateStr) ?? 0;
  };

  // Get color intensity based on activity count
  const getColor = (count: number): string => {
    if (count === 0)
      return "light-dark(var(--mantine-color-gray-1), rgba(255, 255, 255, 0.05))";
    if (count === 1) return "var(--mantine-color-violet-9)";
    if (count === 2) return "var(--mantine-color-violet-7)";
    if (count === 3) return "var(--mantine-color-violet-5)";
    return "var(--mantine-color-violet-3)";
  };

  // Group dates by week
  const weeks: Date[][] = [];
  let currentWeek: Date[] = [];

  // Pad the beginning to align with Sunday
  const firstDate = dates[0];
  if (firstDate) {
    const firstDayOfWeek = getDayOfWeek(firstDate);
    for (let i = 0; i < firstDayOfWeek; i++) {
      currentWeek.push(new Date(0)); // Placeholder for empty cells
    }
  }

  dates.forEach(date => {
    currentWeek.push(date);
    if (getDayOfWeek(date) === 6) {
      // Saturday
      weeks.push(currentWeek);
      currentWeek = [];
    }
  });

  // Add remaining days
  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) {
      currentWeek.push(new Date(0)); // Placeholder
    }
    weeks.push(currentWeek);
  }

  return (
    <Card withBorder radius="lg" p="xl" className="glass-card" bg="transparent">
      <Title order={3} size="h4" mb="xl">
        Activity Heatmap
      </Title>

      <Box style={{overflowX: "auto"}}>
        <Group gap={3} align="flex-start" wrap="nowrap">
          {/* Day labels */}
          <Stack gap={3} mr={8}>
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day, i) => (
              <Text
                key={day}
                size="10px"
                c="dimmed"
                h={12}
                w={30}
                ta="right"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                {i % 2 === 1 ? day : ""}
              </Text>
            ))}
          </Stack>

          {/* Heatmap grid */}
          {weeks.map(week => (
            <Stack key={week[0].getTime()} gap={3}>
              {week.map(date => {
                const count = date.getTime() === 0 ? 0 : getActivityCount(date);
                const isPlaceholder = date.getTime() === 0;
                const dateKey = isPlaceholder
                  ? `placeholder-${Math.random()}`
                  : date.getTime().toString();

                if (isPlaceholder) {
                  return (
                    <Box key={dateKey} w={12} h={12} style={{borderRadius: "2px"}} />
                  );
                }

                return (
                  <Tooltip
                    key={dateKey}
                    label={`${formatHeatmapDate(date)}: ${count} session${count !== 1 ? "s" : ""}`}
                    withArrow
                    position="top"
                  >
                    <Box
                      w={12}
                      h={12}
                      style={{
                        borderRadius: "2px",
                        backgroundColor: getColor(count),
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        border:
                          "1px solid light-dark(var(--mantine-color-gray-3), rgba(255, 255, 255, 0.1))",
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Stack>
          ))}
        </Group>

        {/* Legend */}
        <Group gap={8} mt="xl" align="center">
          <Text size="xs" c="dimmed">
            Less
          </Text>
          <Group gap={3}>
            {[0, 1, 2, 3, 4].map(level => (
              <Box
                key={level}
                w={12}
                h={12}
                style={{
                  borderRadius: "2px",
                  backgroundColor: getColor(level),
                  border:
                    "1px solid light-dark(var(--mantine-color-gray-3), rgba(255, 255, 255, 0.1))",
                }}
              />
            ))}
          </Group>
          <Text size="xs" c="dimmed">
            More
          </Text>
        </Group>
      </Box>
    </Card>
  );
}
